/*
 * Reduce en el navegador las fotos que superan un peso máximo (fotos de celular en
 * alta resolución), sin librerías: redimensiona con canvas y re-codifica en JPEG.
 * Primero baja la calidad y, si no alcanza, la resolución, para conservar la
 * legibilidad del texto de la fórmula el mayor tiempo posible.
 */
export class ImageCompression {

  static readonly COMPRESSIBLE_TYPES = ['image/jpeg', 'image/jpg', 'image/png'];

  private static readonly MAX_SIDES = [2000, 1600, 1280, 1024];
  private static readonly QUALITIES = [0.85, 0.75, 0.65, 0.55];

  static isCompressible(file: File): boolean {
    return ImageCompression.COMPRESSIBLE_TYPES.includes(file.type);
  }

  /** Devuelve un JPEG de máximo `maxBytes`; rechaza si no lo logra. */
  static async compress(file: File, maxBytes: number): Promise<File> {
    const image = await ImageCompression.loadImage(file);
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    const longSide = Math.max(image.naturalWidth, image.naturalHeight);

    for (const maxSide of ImageCompression.MAX_SIDES) {
      const scale = Math.min(1, maxSide / longSide);
      canvas.width = Math.round(image.naturalWidth * scale);
      canvas.height = Math.round(image.naturalHeight * scale);
      // Fondo blanco: JPEG no tiene transparencia y un PNG transparente quedaría negro.
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      for (const quality of ImageCompression.QUALITIES) {
        const blob = await ImageCompression.toBlob(canvas, quality);
        if (blob && blob.size <= maxBytes) {
          return new File([blob], ImageCompression.jpgName(file.name), { type: 'image/jpeg', lastModified: Date.now() });
        }
      }
    }
    throw new Error('No fue posible reducir la imagen al tamaño máximo');
  }

  // Los navegadores actuales aplican la orientación EXIF al dibujar el <img>,
  // así la foto vertical del celular no queda acostada.
  private static loadImage(file: File): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        URL.revokeObjectURL(url);
        resolve(image);
      };
      image.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('No fue posible leer la imagen'));
      };
      image.src = url;
    });
  }

  private static toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
  }

  private static jpgName(name: string): string {
    const dot = name.lastIndexOf('.');
    return (dot > 0 ? name.substring(0, dot) : name) + '.jpg';
  }
}
